import { getLocalCurrentUser, DIRECT_REPORT_IDS_KEY } from "./offlineBoot";
import { MATERIALS_CACHE_KEY } from "./materialsCatalog";
import { PROJECT_FIELD_SNAPSHOT_KEY } from "./projectFieldSnapshot";
import { PROJECT_TASKS_CACHE_KEY } from "./tasks/tasksCache";
import { PROJECT_CACHE_KEY, getCachedProjects, saveCachedProjects } from "./projectVisibility";
import { isProjectAccessibleToActor } from "./teamScope";
import {
  CACHE_ORG_KEY,
  getActiveCacheOrganizationId,
  readOrgScopedJson,
  removeOrgScopedKeysForBase,
  resolveCacheOrganizationId,
  stampActiveCacheOrganization,
  writeOrgScopedJson,
} from "./orgCacheStorage";

export const CACHE_OWNER_KEY = "CP_CACHE_OWNER";
export const PENDING_TIMELOGS_KEY = "CP_PENDING_TIMELOGS";
export const SESSION_MATERIALS_KEY = "CP_SESSION_MATERIALS";

export const CLASSIFICATIONS_CACHE_KEY = "CP_CLASSIFICATIONS_CACHE_V1";
export const PAYROLL_TYPES_CACHE_KEY = "CP_PAYROLL_TYPES_CACHE_V1";
export const ORG_SETTINGS_CACHE_KEY = "CP_ORG_SETTINGS_V1";
export const PROJECT_FAVORITES_KEY = "CP_PROJECT_FAVORITES";

const SCOPED_CACHE_KEYS = [
  PROJECT_CACHE_KEY,
  PROJECT_FIELD_SNAPSHOT_KEY,
  PROJECT_FAVORITES_KEY,
  PROJECT_TASKS_CACHE_KEY,
  MATERIALS_CACHE_KEY,
  PENDING_TIMELOGS_KEY,
  SESSION_MATERIALS_KEY,
  CLASSIFICATIONS_CACHE_KEY,
  PAYROLL_TYPES_CACHE_KEY,
  ORG_SETTINGS_CACHE_KEY,
  "CP_WORK_TYPES_CACHE_V1",
  "CP_PENDING_ACTIVE_SESSION_DELETES",
];

export {
  buildOrgScopedCacheKey,
  getActiveCacheOrganizationId,
  ORG_CACHE_SEP,
  resolveCacheOrganizationId,
  stampActiveCacheOrganization,
} from "./orgCacheStorage";

export function getOfflineCacheOwnerId() {
  try {
    return localStorage.getItem(CACHE_OWNER_KEY);
  } catch {
    return null;
  }
}

export function stampOfflineCacheOwner(supabaseUserId) {
  if (!supabaseUserId) {
    return;
  }
  localStorage.setItem(CACHE_OWNER_KEY, supabaseUserId);
}

export function clearScopedOfflineCaches() {
  localStorage.removeItem(CACHE_OWNER_KEY);
  localStorage.removeItem(CACHE_ORG_KEY);
  for (const key of SCOPED_CACHE_KEYS) {
    removeOrgScopedKeysForBase(key);
  }
}

/**
 * When a different user signs in, drop role-scoped caches to prevent stale privileged data.
 */
export function reconcileOfflineCachesForUser(actor) {
  const actorId = actor?.supabase_user_id;
  if (!actorId) {
    return;
  }

  const previousOwner = getOfflineCacheOwnerId();
  if (previousOwner && previousOwner !== actorId) {
    clearScopedOfflineCaches();
    localStorage.removeItem(DIRECT_REPORT_IDS_KEY);
  }

  stampOfflineCacheOwner(actorId);
}

/**
 * On organization switch, point reads/writes at the new org bucket without deleting other org caches.
 */
export function reconcileOfflineCachesForOrganization(
  actor,
  previousOrganizationId = null,
) {
  const nextOrgId = resolveCacheOrganizationId(actor);
  if (!nextOrgId) {
    return {
      switched: false,
      previousOrganizationId: previousOrganizationId ?? getActiveCacheOrganizationId(),
      activeOrganizationId: null,
    };
  }

  const previous =
    previousOrganizationId ?? getActiveCacheOrganizationId() ?? null;
  stampActiveCacheOrganization(nextOrgId);

  return {
    switched: Boolean(previous && previous !== nextOrgId),
    previousOrganizationId: previous,
    activeOrganizationId: nextOrgId,
  };
}

export function reconcileOfflineCachesForActor(actor, options = {}) {
  reconcileOfflineCachesForUser(actor);
  return reconcileOfflineCachesForOrganization(
    actor,
    options.previousOrganizationId ?? null,
  );
}

export function filterProjectsForActor(projects, actor, directReportIds = []) {
  return (projects || []).filter((project) =>
    isProjectAccessibleToActor(actor, project, directReportIds),
  );
}

export function getScopedCachedProjects(actor, directReportIds = []) {
  const organizationId = resolveCacheOrganizationId(actor);
  const cached = getCachedProjects(organizationId);
  if (!cached?.length) {
    return [];
  }
  return filterProjectsForActor(cached, actor, directReportIds);
}

export function saveScopedCachedProjects(projects, actor, directReportIds = []) {
  const organizationId = resolveCacheOrganizationId(actor);
  saveCachedProjects(
    filterProjectsForActor(projects, actor, directReportIds),
    organizationId,
  );
}

function resolvePendingTimelogOrganizationId(organizationId, actor) {
  return organizationId ?? resolveCacheOrganizationId(actor);
}

export function readPendingTimelogs(organizationId = null, actor = null) {
  const orgId = resolvePendingTimelogOrganizationId(organizationId, actor);
  try {
    const parsed = readOrgScopedJson(PENDING_TIMELOGS_KEY, orgId);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function writePendingTimelogs(rows, organizationId = null, actor = null) {
  const orgId = resolvePendingTimelogOrganizationId(organizationId, actor);
  writeOrgScopedJson(PENDING_TIMELOGS_KEY, orgId, rows || []);
}

/**
 * Pending timelogs are self-owned and org-scoped — reject cross-user rows on read/write.
 */
export function filterPendingTimelogsForActor(pending, actor) {
  const actorId = actor?.supabase_user_id;
  if (!actorId) {
    return [];
  }

  const activeOrgId = resolveCacheOrganizationId(actor);

  return (pending || []).filter((log) => {
    const ownerId = log?.supabase_user_id || log?.user_id;
    if (ownerId !== actorId) {
      return false;
    }
    if (log?.organization_id && activeOrgId) {
      return log.organization_id === activeOrgId;
    }
    return true;
  });
}

export function stampPendingTimelogForQueue(log, actor) {
  const organizationId = resolveCacheOrganizationId(actor);
  if (!organizationId || !log) {
    return log;
  }
  return {
    ...log,
    organization_id: log.organization_id ?? organizationId,
  };
}

export function getScopedPendingTimelogs(actor = getLocalCurrentUser()) {
  return filterPendingTimelogsForActor(readPendingTimelogs(null, actor), actor);
}

export function persistScopedPendingTimelogs(pending, actor = getLocalCurrentUser()) {
  const scoped = filterPendingTimelogsForActor(pending, actor);
  writePendingTimelogs(scoped, null, actor);
}
